import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LiveMessagingService } from '@headpower/layout';
import { forkJoin, Subject, throwError, catchError, takeUntil, of } from 'rxjs';

import { ExternalAreaImportDialogData, ExternalAreaImportRequest } from '../external-request.model';
import { ExternalRequestService } from '../external-request.service';
import { ExternalRequest, ExternalRequestTargetType } from '../external-request.model';

@Component({
    selector: 'app-external-area-import-dialog',
    templateUrl: './external-area-import-dialog.component.html',
    styleUrls: ['./external-area-import-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalAreaImportDialogComponent implements OnInit, OnDestroy {

    public mobile: boolean = false;
    public initializing: boolean = true;
    public submitting: boolean = false;

    public targetId!: string;
    public targetType!: ExternalRequestTargetType;

    public areaId: string = '';
    public nisId: string = '';

    public externalAreaImports: ExternalRequest[] = [];

    public get isFormValid(): boolean {
        return !!this.areaId &&
            !!this.nisId;
    }

    private destroy$: Subject<void> = new Subject();

    constructor(
        private dialogRef: MatDialogRef<ExternalAreaImportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private dialogData: ExternalAreaImportDialogData,
        private externalRequestService: ExternalRequestService,
        private liveMessagingService: LiveMessagingService,
        private cdRef: ChangeDetectorRef) { }

    ngOnInit() {
        this.mobile = this.dialogData.mobile;
        this.targetId = this.dialogData.targetId;
        this.targetType = this.dialogData.targetType;

        this.fetchInitialData();
        this.listenLiveMessages();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public submit() {
        if (!this.isFormValid) {
            return;
        }

        this.submitting = true;

        const config: ExternalAreaImportRequest = {
            TargetId: this.targetId,
            TargetType: this.targetType,
            AreaId: this.areaId,
            NisId: this.nisId
        };

        this.externalRequestService.requestExternalAreaImport(config)
            .pipe(
                catchError(error => {
                    this.submitting = false;
                    this.cdRef.markForCheck();

                    return throwError(() => error);
                })
            )
            .subscribe(() => {
                this.dialogRef.close();
            });
    }

    private fetchInitialData() {
        forkJoin([
            this.externalRequestService.getAttachmentLinkValue(this.targetType, this.targetId, 'NISID'),
            this.externalRequestService.getExternalRequestHistory(this.targetType, this.targetId, 'ExternalAreaImport')
        ])
            .subscribe(([nisId, externalAreaImports]) => {
                if (nisId) {
                    this.nisId = nisId;
                }

                this.externalAreaImports = externalAreaImports;

                this.initializing = false;
                this.cdRef.markForCheck();
            });
    }

    private fetchExternalAreaImportHistory() {
        this.externalRequestService.getExternalRequestHistory(this.targetType, this.targetId, 'ExternalAreaImport')
            .subscribe(result => {
                this.externalAreaImports = result;

                this.cdRef.markForCheck();
            });
    }

    private listenLiveMessages() {
        const topic = `iwm/externalRequest/externalAreaImport/${this.targetId}`;

        this.liveMessagingService.listenToSingleMessages(topic)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                // If request changes refresh the history
                this.fetchExternalAreaImportHistory();
            });
    }
}
